import React, { useEffect, useMemo, useState } from 'react'
import { NumberInput, SelectArrayInput, TextInput, minValue, number, required, useNotify, useRecordContext } from 'react-admin'
import { useWatch } from 'react-hook-form'

import { httpClient } from '../../../../../config'
import { useIndustry } from '../../../../../context/IndustryContext'
import { CRON_CONFIG_FORM_KEY } from '../../../../../helpers/cronConfigSchemas'
import { getCronConfigSchema } from '../../../../../utils/cronConfigUtils'
import { validateInteger, validateJsonField } from '../../../../../utils/validationUtils'

export const ReportsSelectInput = ({ source, label, helperText }) => {
   const [choices, setChoices] = useState(null)
   const [loadFailed, setLoadFailed] = useState(false)
   const { industries } = useIndustry()
   const notify = useNotify()
   const selectedValue = useWatch({ name: source })

   useEffect(() => {
      let cancelled = false
      httpClient('/access/data')
         .then(response => {
            if (!cancelled) setChoices(response.json.reportData)
         })
         .catch(() => {
            if (cancelled) return
            notify('Could not load the report list', { type: 'error' })
            setLoadFailed(true)
            setChoices([])
         })
      return () => {
         cancelled = true
      }
   }, [notify])

   const allChoices = useMemo(() => {
      if (choices === null) return []
      const selected = Array.isArray(selectedValue) ? selectedValue : []
      const industryName = industryId => {
         const industry = (industries || []).find(({ id }) => id === industryId)
         if (!industry || !industry.name) return `industry ${industryId}`
         return industry.name.charAt(0).toUpperCase() + industry.name.slice(1)
      }
      const list = choices
         .map(({ id, report_name, industry_id }) => ({ id, name: `${report_name} (${industryName(industry_id)})` }))
         .sort((a, b) => a.name.localeCompare(b.name))
      selected.forEach(id => {
         if (!list.some(choice => choice.id === id)) {
            list.push({ id, name: loadFailed ? `Report #${id}` : `Report #${id} (hidden or deleted)` })
         }
      })
      return list
   }, [choices, selectedValue, industries, loadFailed])

   return (
      <SelectArrayInput
         source={source}
         label={label}
         helperText={helperText}
         choices={allChoices}
         isLoading={choices === null}
         defaultValue={[]}
         fullWidth
      />
   )
}

export const CronConfigInputs = () => {
   const record = useRecordContext()
   const watched = useWatch({ name: 'cron_name' })
   const cronName = watched ?? record?.cron_name
   const schema = getCronConfigSchema(cronName)

   if (!schema) {
      return (
         <TextInput
            source="config"
            label="Config"
            helperText='Please enter values in JSON format, for example: {"limit":100}'
            multiline
            fullWidth
            validate={validateJsonField}
         />
      )
   }

   return (
      <>
         {schema.map(field => {
            const source = `${CRON_CONFIG_FORM_KEY}.${field.key}`
            if (field.type === 'reports') {
               return (
                  <ReportsSelectInput
                     key={field.key}
                     source={source}
                     label={field.label}
                     helperText={field.helperText}
                  />
               )
            }
            return (
               <NumberInput
                  key={field.key}
                  source={source}
                  label={field.label}
                  helperText={field.helperText}
                  defaultValue={field.defaultValue}
                  step={1}
                  validate={[required(), number(), validateInteger, minValue(field.min)]}
               />
            )
         })}
      </>
   )
}
